import React, { useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { AppBar, Toolbar, Button, makeStyles, Box, Theme, Container } from '@material-ui/core';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';
import { useAuth0 } from '@auth0/auth0-react';
import LogoName from './LogoName';
import ProfileButton from './ProfileButton';
import { useUser, ConnectorRoleData } from '../contexts/UserContext';
import { Role, TypographyColor } from '../types/types';
import { StyledText } from './StyledText';
import InviteEntsModal from '../pages/connector/InviteEntsModal';
import API, { InviteEntRequestResponse } from '../api/api';
import { useAlert } from '../contexts';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minWidth: theme.breakpoints.values.md,
    margin: 0,
    width: '100%',
    maxWidth: '100%'
  },
  appBar: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    background: '#FFFFFF',
    boxShadow: 'none',
    borderBottom: '1px #DAD9DF solid'
  },
  toolBar: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
  },
  menuItem: {
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.secondary.main
    }
  }
}));

interface RoleMenuElementConfig {
  label: string
  color: TypographyColor
  fontWeight: number
}

export enum SelectedEntTab {
  PROJECT = 0,
  INVESTORS = 1
}

export enum SelectedConnectorsTab {
  DEALS = 0,
  NETWORK = 1,
  UNSELECTED = 2
}

const getConnTabIndex = (path: string): SelectedConnectorsTab => {
  if (path.startsWith(CONNECTOR_DEALS_PATH)) {
    return SelectedConnectorsTab.DEALS;
  } else if (path.startsWith(CONNECTOR_NETWORK_PATH)) {
    return SelectedConnectorsTab.NETWORK;
  }

  return SelectedConnectorsTab.UNSELECTED;
};

const createStatusMessageForEntInviteResult = (result: InviteEntRequestResponse) => {
  const { invitesToUnpublishedEnts,
    dealsWithPublishedEnts,
    nonEntExistingUserErrors,
    sentInvites } = result;

  if ((invitesToUnpublishedEnts.length + dealsWithPublishedEnts.length + nonEntExistingUserErrors.length) === 0) {
    return 'Invites were sent!';
  }

  let message = '';

  if (dealsWithPublishedEnts.length > 0) {
    message = `Deals were created with some existing Capital Raise user(s): ${dealsWithPublishedEnts.join(', ')}.`;
  }

  if (invitesToUnpublishedEnts.length > 0) {
    if (message !== '') {
      message += `
      `;
    }
    const countGreaterThan1 = invitesToUnpublishedEnts.length > 1;
    message += `${countGreaterThan1 ? 'Profiles exist' : 'A profile exists'} for the following ${countGreaterThan1 ? 'users' : 'user'} but ${countGreaterThan1 ? 'have' : 'has'} not been completed yet: ${invitesToUnpublishedEnts.join(', ')}. 

    ${countGreaterThan1 ? 'Deals with those users' : 'A deal with that user'} will be added once their ${countGreaterThan1 ? 'profiles are' : 'profile is'} complete.`;
  }

  if (nonEntExistingUserErrors.length > 0) {
    if (message !== '') {
      message += `
      `;
    }
    message += `Errors were encountered for the following attempted invites: ${nonEntExistingUserErrors.join(', ')}.`;
  }

  if (sentInvites.length > 0 && message !== '') {
    message += '\n All other invites were sent.';
  }

  return message;
};

const getEntTabIndex = (path: string): SelectedEntTab => {
  if (path.includes('matched-investors')) {
    return SelectedEntTab.INVESTORS;
  } else {
    return SelectedEntTab.PROJECT;
  }
};


const CONNECTOR_PROFILE_PATH = '/connector';
const CONNECTOR_NETWORK_PATH = '/connector/network';
const CONNECTOR_DEALS_PATH = '/connector/deals';
const ENTREPRENEUR_PATH = '/entrepreneur';

const connPathsByIndex = [CONNECTOR_DEALS_PATH, CONNECTOR_NETWORK_PATH];

interface RoleMenuElementProps {
  role: string
  profileId: number
}

const RoleMenuElement:React.FC<RoleMenuElementProps> = ({ role, profileId }) => {
  const menuItem1: RoleMenuElementConfig = { label: '', color: TypographyColor.textPrimary, fontWeight: 300 };
  const menuItem2: RoleMenuElementConfig = { label: '', color: TypographyColor.textPrimary, fontWeight: 300 };
  const { pathname } = useLocation();
  const history = useHistory();
  const styles = useStyles();
  if (role === Role.Connector) {
    const selectedConnectorsTab = getConnTabIndex(pathname);
    menuItem1.label = 'Deals';
    menuItem2.label = 'Network';
    menuItem1.color = selectedConnectorsTab === 0 ? TypographyColor.secondary : TypographyColor.textPrimary;
    menuItem2.color = selectedConnectorsTab === 1 ? TypographyColor.secondary : TypographyColor.textPrimary;
    menuItem1.fontWeight = selectedConnectorsTab === 0 ? 500 : 300;
    menuItem2.fontWeight = selectedConnectorsTab === 1 ? 500 : 300;
  }
  if (role === Role.Ent) {
    const selectedEntTab = getEntTabIndex(pathname);
    menuItem1.label = 'My Company';
    menuItem2.label = 'Matched Investors';
    menuItem1.color = selectedEntTab === 0 ? TypographyColor.secondary : TypographyColor.textPrimary;
    menuItem2.color = selectedEntTab === 1 ? TypographyColor.secondary : TypographyColor.textPrimary;
    menuItem1.fontWeight = selectedEntTab === 0 ? 500 : 300;
    menuItem2.fontWeight = selectedEntTab === 1 ? 500 : 300;
  }

  const handleClick = (idx: number) => {
    if (role === Role.Ent) {
      if (idx === 1) {
        history.push(`${ENTREPRENEUR_PATH}/${profileId}/matched-investors/recommended`);
      } else {
        history.push(`${ENTREPRENEUR_PATH}/${profileId}`);
      }
    } else {
      history.push(connPathsByIndex[idx]);
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  if (role === Role.Ent) {
    return (
      <Box display='flex' flexGrow='1' alignItems='center'>
        <LogoName onClick={() => { handleClick(0); }} />
      </Box>
    );
  }

  return (
    <Box display='flex' flexGrow='1' alignItems='center'>
      <LogoName onClick={() => { handleClick(0); }} />
      <StyledText className={styles.menuItem} variant='h5' component='h6' color={menuItem1.color} style={{ fontWeight: menuItem1.fontWeight }} onClick={() => { handleClick(0); }}>
        { menuItem1.label }
      </StyledText>
      <StyledText className={styles.menuItem} variant='h5' component='h6' color={menuItem2.color} style={{ fontWeight: menuItem2.fontWeight }} onClick={() => { handleClick(1); }}>
        { menuItem2.label }
      </StyledText>
    </Box>
  );
};

const NavBar = () => {
  const styles = useStyles();
  const { loginWithRedirect, isAuthenticated, logout } = useAuth0();
  const handleAuth = () => {
    if (isAuthenticated) {
      logout();
    } else {
      loginWithRedirect();
    }
  };
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window
  });
  const { userInfo, roleSpecificData } = useUser();
  const userRole = userInfo?.role;
  const profileId = userInfo?.profileId;
  const history = useHistory();
  const shouldShowInviteEntPrompt = userRole && userRole === Role.Connector && (roleSpecificData && !roleSpecificData.profileIncomplete);

  const handleGoToProfile = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!userRole || !profileId) {
      return;
    }
    if (userRole === Role.Ent) {
      history.push(`${ENTREPRENEUR_PATH}/${profileId}`);
    } else {
      history.push(`${CONNECTOR_PROFILE_PATH}/${profileId}`);
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const { getAccessToken } = useUser();
  const { postNewAlert } = useAlert();

  const [invitingEnts, setInvitingEnts] = useState(false);
  type EntsInviteInput = { inviteMessage: string, entEmails: string[] };

  const handleInviteEntsSubmitted = async (input: EntsInviteInput) => {
    const { sendEntInviteToJoin } = API;
    try {
      const token = await getAccessToken();
      const response = await sendEntInviteToJoin(token, input);
      const alertMessage = createStatusMessageForEntInviteResult(response);
      postNewAlert(alertMessage);
      setInvitingEnts(false);
    } catch (error: unknown) {
      postNewAlert('An error occurred sending entrepreneur invites.');
    }
  };

  // this is used as the default value for the message and can only be set once, so need to make sure
  // it is fully defined before initializing the InviteEntsModal
  const inviteEntMessage = useMemo(() => (shouldShowInviteEntPrompt ? 'Dear Entrepreneur,\n\n'
  + 'I\'d like to refer your company to my personal network of investors through Capital Raise. There is no cost for you to join – all you have to do is sign up and complete your profile.\n\n'
  + 'The moment you complete your profile, the platform will show you all the investors who are most likely to invest in your company. You can request warm, personalized introductions to any of those investors from connectors like me.\n\n'
  + 'Thanks,\n'
  + `${(roleSpecificData as ConnectorRoleData)?.fullName}` : ''), [roleSpecificData, shouldShowInviteEntPrompt]);

  return (
    <Container className={styles.root} disableGutters>
      { shouldShowInviteEntPrompt && inviteEntMessage !== ''
            && (
              <InviteEntsModal
                open={invitingEnts}
                defaultMessage={inviteEntMessage}
                onClose={() => { setInvitingEnts(false); }}
                handleSubmit={handleInviteEntsSubmitted}
              />
            )}
      <AppBar className={styles.appBar} elevation={trigger ? 4 : 0}>
        <Toolbar className={styles.toolBar} disableGutters>
          {!userRole && !profileId && <LogoName />}
          {userRole && profileId && <RoleMenuElement role={userRole} profileId={profileId} /> }
          { shouldShowInviteEntPrompt
            && (
            <StyledText className={styles.menuItem} variant='h5' component='h6' color='primary' style={{ fontWeight: 300 }} onClick={() => { setInvitingEnts(!invitingEnts); }}>
              Invite an entrepreneur
            </StyledText>
            )}
          <Box display='flex' alignItems='center'>
            { isAuthenticated
              ? [<ProfileButton onLogout={handleAuth} goToProfile={handleGoToProfile} user={userInfo || undefined} key='not-1' />]
              : (
                [
                  <Button
                    color='primary'
                    onClick={handleAuth}
                  >
                    Login
                  </Button>
                ]
              )}
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar />
    </Container>
  );
};

export default NavBar;
